import * as React from "react";
import { toast } from "react-toastify";
import {
  CellContext,
  ColumnDef
} from "@tanstack/react-table";

import { FilterParams } from "src/library/utils";
import {
  ActionButton,
  Button
} from "src/library/components/Button";
import { LikeToDo } from "src/library/components/LikeToDo";
import { Modal } from "src/library/components/Modal";
import {
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import { PaginatedParams } from "src/core/shared/api";
import { PageProps } from "src/pages/Router/routes";
import {
  useShutdownsApi,
  IShutdown
} from "src/modules/shutdowns/api/shutdown.api";
import { shutdownsTableColumns } from "src/core/shared/components/tables/shutdownsTableColumns";
import useCheckUserPermission from "src/core/users/hooks/useCheckUserPermission";
import { Table } from "src/core/dashboard/components/widgets";
import { TableRefProps } from "src/core/dashboard/components/widgets/table/Table";

const defaultPagination: PaginatedParams<IShutdown> = {
  page: 0,
  limit: 20,
  offset: 0,
  sort: [ [ "startDate", "DESC" ] ]
};

/**
 * View Shutdowns
 *
 */
const ShutdownsPage: React.ReactFCWithChildren<PageProps<unknown>> = props => {
  const { history } = props;
  const TableRef = React.useRef<TableRefProps>(null);
  const [ shutdownsResponse, getShutdowns ] = useShutdownsApi("LIST_SHUTDOWNS");
  const shutdownsData = React.useMemo(() => shutdownsResponse.data?.result || [], [ shutdownsResponse.data?.result ]);
  const [ deleteShutdownResponse, deleteShutdown ] = useShutdownsApi("DELETE_SHUTDOWN");
  const [ modalData, setModalData ] = React.useState<number | null>(null);
  const checkPermission = useCheckUserPermission();
  const canCreate = checkPermission("create-global-settings");
  const canAmend = checkPermission("amend-global-settings");
  const canDelete = checkPermission("delete-global-settings");

  const onFetchData = React.useCallback((params: {filters: FilterParams<IShutdown>, pagination: PaginatedParams<IShutdown>}) => {
    const { filters, pagination } = params;

    getShutdowns({
      pagination,
      filters
    });
  }, [ getShutdowns ]);

  const editShutdownAction = React.useCallback((id: number) => {
    history.push(`/platform/shutdowns/edit/${id}`);
  }, [ history ]);

  const deleteShutdownAction = (id: number) => {
    setModalData(id);
  };

  const deleteShutdownActionConfirm = () => {
    if (modalData) {
      deleteShutdown(modalData);
      setModalData(null);
    }
  };

  // On shutdown deletion
  React.useEffect(() => {
    if (deleteShutdownResponse.data) {
      toast.success("The shutdown has been successfully deleted.");

      if (TableRef.current) {
        TableRef.current.reloadTable();
      }
    }

    if (deleteShutdownResponse.error) {
      toast.error(`Could not delete shutdown - ${deleteShutdownResponse.error.message}`);
    }
  }, [ deleteShutdownResponse ]);

  /**
   * Table actions
   */
  const tableActions = React.useCallback(({ row }: CellContext<IShutdown, unknown>) => {
    const actions = [];

    if (canAmend) {
      actions.push({
        title: "Edit",
        action: () => editShutdownAction(row.original.id)
      });
    }

    if (canDelete) {
      actions.push({
        title: "Delete",
        action: () => deleteShutdownAction(row.original.id)
      });
    }

    return actions;
  }, [
    canAmend,
    canDelete,
    editShutdownAction
  ]);

  const tableColumns: Array<ColumnDef<IShutdown, keyof IShutdown>> = React.useMemo(() => {
    return shutdownsTableColumns(row => {
      const actions = tableActions(row);

      return actions.length ? (
        <ActionButton
          title="Actions"
          items={tableActions(row)}
          loading={deleteShutdownResponse.pending}
        />
      ) : null;
    },

    ) ;
  }, [ tableActions, deleteShutdownResponse.pending ]);

  return (
    <Layout
      {...props}
      title="Shutdowns"
    >
      <MainContentWrapper
        fill
        Header={canCreate && (
          <LikeToDo
            copy="What would you like to do?"
            buttons={[
              <Button
                key="new-shutdown"
                onClick={() => history.push("/platform/shutdowns/new")}
              >
                New shutdown
              </Button>
            ]}
          />
        )}
      >
        <Table
          tableOf="Shutdowns"
          className="large"
          data={shutdownsData}
          onFetchData={onFetchData}
          defaultPagination={defaultPagination}
          defaultVisibility={{ id: false }}
          loading={shutdownsResponse.pending}
          pageCount={shutdownsResponse.data?.pages}
          rowCount={shutdownsResponse.data?.count}
          error={shutdownsResponse.error}
          columns={tableColumns}
          mainAction={row => editShutdownAction(row.original.id)}
        />
      </MainContentWrapper>

      <Modal
        small
        show={modalData !== null}
        type="confirm"
        title="Are you sure?"
        body="Deleting this shutdown cannot be undone."
        backgroundAction={() => setModalData(null)}
        actions={[
          {
            name: "No, cancel",
            action: () => setModalData(null),
            $buttonType: "secondary"
          },
          {
            name: "I confirm",
            action: deleteShutdownActionConfirm,
            $buttonType: "primary"
          }
        ]}
      />
    </Layout>
  );
};

export default ShutdownsPage;
